import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import Cta from "@/components/ui-kit/Cta";
import { useConsent } from "@/lib/consent";

const CATEGORIES = [
  {
    key: "necessary",
    name: "Strictly Necessary",
    always: true,
    desc: "Required for the site to work, such as security and remembering your consent choice. Always on.",
  },
  {
    key: "analytics",
    name: "Analytics",
    always: false,
    desc: "Helps us understand which pages are useful. No analytics script loads until you allow it.",
  },
  {
    key: "marketing",
    name: "Marketing",
    always: false,
    desc: "Used to measure campaigns. No marketing script loads until you allow it.",
  },
];

export default function CookieConsent() {
  const { decided, prefs, prefsOpen, setPrefsOpen, acceptAll, rejectAll, savePrefs } = useConsent();
  const [local, setLocal] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (prefsOpen) setLocal({ analytics: !!prefs.analytics, marketing: !!prefs.marketing });
  }, [prefsOpen, prefs]);

  const showBanner = !decided;

  return (
    <>
      {showBanner && (
        <div
          className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5"
          role="region"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div className="mx-auto flex max-w-4xl flex-col gap-4 rounded-2xl border border-etap-deep-100 bg-white p-5 shadow-etap-xl sm:p-6 md:flex-row md:items-center">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-etap-primary-50 text-etap-primary">
                <Cookie className="h-5 w-5" aria-hidden="true" />
              </span>
              <p className="text-sm leading-relaxed text-etap-deep-700">
                We use cookies to run the site and, with your permission, to understand usage. You can accept, reject, or
                choose. Read our{" "}
                <Link to="/cookie-policy" className="font-semibold text-etap-primary underline underline-offset-2">
                  Cookie Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row md:shrink-0">
              <button
                type="button"
                onClick={() => setPrefsOpen(true)}
                className="h-11 rounded-full border border-etap-deep-100 px-5 text-sm font-semibold text-etap-deep-700 transition-colors hover:bg-etap-primary-50"
                data-testid="cookie-manage"
              >
                Manage Preferences
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="h-11 rounded-full border border-etap-primary/40 bg-white px-5 text-sm font-semibold text-etap-deep-700 transition-colors hover:border-etap-primary hover:bg-etap-primary-50"
                data-testid="cookie-reject-all"
              >
                Reject All
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="h-11 rounded-full bg-etap-primary px-5 text-sm font-semibold text-white shadow-etap-md transition-colors hover:bg-etap-deep"
                data-testid="cookie-accept-all"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-w-lg rounded-3xl" data-testid="cookie-preferences">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl text-etap-deep-800">
              <ShieldCheck className="h-6 w-6 text-etap-primary" aria-hidden="true" />
              Cookie preferences
            </DialogTitle>
            <DialogDescription className="text-etap-deep-600">
              Choose what you allow. Nothing beyond strictly necessary loads without your consent.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-3">
            {CATEGORIES.map((c) => (
              <div
                key={c.key}
                className="flex items-start justify-between gap-4 rounded-2xl border border-etap-deep-100 bg-etap-deep-50/60 p-4"
              >
                <div>
                  <p className="font-display text-sm font-bold text-etap-deep-800">{c.name}</p>
                  <p className="mt-1 text-xs leading-relaxed text-etap-deep-600">{c.desc}</p>
                </div>
                {c.always ? (
                  <span className="shrink-0 rounded-full bg-etap-mint-200 px-3 py-1 text-xs font-bold text-etap-deep-700">
                    Always on
                  </span>
                ) : (
                  <Switch
                    checked={local[c.key]}
                    onCheckedChange={(v) => setLocal((p) => ({ ...p, [c.key]: v }))}
                    className="data-[state=checked]:bg-etap-primary"
                    aria-label={`Toggle ${c.name}`}
                    data-testid={`cookie-toggle-${c.key}`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={rejectAll}
              className="h-11 rounded-full border border-etap-deep-100 px-5 text-sm font-semibold text-etap-deep-700 transition-colors hover:bg-etap-primary-50"
              data-testid="cookie-pref-reject"
            >
              Reject All
            </button>
            <button
              type="button"
              onClick={() => savePrefs(local)}
              className="h-11 rounded-full border border-etap-primary/40 px-5 text-sm font-semibold text-etap-deep-700 transition-colors hover:border-etap-primary hover:bg-etap-primary-50"
              data-testid="cookie-pref-save"
            >
              Save Preferences
            </button>
            <Cta onClick={acceptAll} variant="primary" testId="cookie-pref-accept">
              Accept All
            </Cta>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
